import { Context } from '../../server-middleware/context'
import { Component } from 'preact'
import MenuMain from '../menu/main.sync'
import { z } from 'zod'
import { CurrentUserContext } from '../../server-middleware/userContext'
import Phrase from '../../libs/translationsUtils/phrase'
import ClientComponent from '../client-component/index.server'
import { getCategories, prepCategories } from '../../server-middleware/categoryRepository'
import { CategoryInfo } from '../mega-menu/Types'
import getConfig from '../../services/configCommunity'
import { GeneralDbConfig } from 'js/src/services/generalConfig'
import Dropdown from '../User/Dropdown'
import SearchForm from '../algolia/components/SearchForm'
import classnames from 'classnames'

interface Props {
  categoryGroups: CategoryInfo[]
  knowledgeBaseCategories: CategoryInfo[]
  ssoLoginUrl: string
  isFederatedSalesforceSearch: boolean
  isFederatedFreshdeskSearch: boolean
  isFederatedSkilljarSearch: boolean
}

export const widgetName = 'navigation_bar'

export const config = z.undefined()

function isTopicNew () {
  if (typeof window === 'undefined') return null
  return window.location.pathname.match(/^\/topic\/new/)
}

export function decodeSsoUrl (c: GeneralDbConfig['config']['params']): string {
  // taken from src/InSided/Twig/Extension/BrandedExtension.php

  const ssoConfig = c.sso
  if (c.memberlogin?.active) return ''
  if (!ssoConfig?.active) return ''
  if (ssoConfig.proxy) return ssoConfig.login_url || ''

  if (ssoConfig?.twitter?.active) return ''
  if (ssoConfig?.facebook?.active) return ''
  if (ssoConfig?.linkedin?.active) return ''
  if (ssoConfig?.google?.active) return ''

  const customerSsoTypes = [
    'saml',
    'oracle',
    'token',
    'oauth_nos',
    'oauth_soundcloud',
    'oauth_tuenti',
    'openidconnect',
    'oauth2'
  ] as const

  for (const type of customerSsoTypes) {
    if (ssoConfig[type]?.active) return 'sso/login?ssoType=' + type
  }
  return ''
}

export const getServerSideProps = async (context: Context, _props: z.infer<typeof config>) => {
  return await context.cache(`navigation_bar_${JSON.stringify(config)}_${context.userId}`, async () => {
    const list = await prepCategories(context, await getCategories(context))
    const [isFederatedSalesforceSearch] = (await Promise.allSettled([
      getConfig(context, 'salesforceFederatedSearchEnabled')
    ])).map((result) => {
      if (result.status === 'fulfilled') return result.value || false
      return false
    })

    return {
      categoryGroups: list.filter(c => c.type !== 1),
      knowledgeBaseCategories: list.filter(c => c.type === 1),
      isFederatedSalesforceSearch: isFederatedSalesforceSearch,
      isFederatedFreshdeskSearch: context.config.config.params.freshdesk?.enabledFederatedSearch || false,
      isFederatedSkilljarSearch: context.config.config.params.skilljar?.enabledFederatedSearch || false,
      ssoLoginUrl: decodeSsoUrl(context.config.config.params)
    }
  })
}

function shouldShowSearch(): boolean {
  if (typeof window === 'undefined') return false
  return document.querySelector('.searchbar-in-hero') === null && location.pathname !== '/search'
}

class NavigationBar extends Component<Props> {
  render () {
    return (
      <CurrentUserContext.Consumer>{user => {
        return (
          <nav className={classnames('main-navigation--wrapper', 'qa-main-navigation', 'custom-navigation')}>
            <div className='main-navigation-sitewidth'>

              {ClientComponent(MenuMain, 'menu/main', {
                communityCategories: this.props.categoryGroups,
                knowledgeBaseCategories: this.props.knowledgeBaseCategories,
                showActivitySinceLastVisit: !!user.id
              })}

              {shouldShowSearch() && <section className='main-navigation--search-wrapper is-hidden-mobile-only'>
                {ClientComponent(SearchForm, 'algolia/components/SearchForm', {
                  isUserGuest: !user.id,
                  isFederatedSalesforceSearch: this.props.isFederatedSalesforceSearch,
                  isFederatedSkilljarSearch: this.props.isFederatedSkilljarSearch,
                  isFederatedFreshdeskSearch: this.props.isFederatedFreshdeskSearch
                })}
              </section>}

              <section className='main-navigation--nav-buttons-wrapper' data-view='MainNavigation'>
                <ul>
                  {!isTopicNew()
                    ? <li className='is-hidden-S'>
                      <a
                        href='/topic/new'
                        data-track={JSON.stringify({ trigger: 'navigation', type: 'Topic Initiated' })}
                        data-ga-track={JSON.stringify({ eventCategory: 'Homepage', eventAction: 'Create topic clicked', eventLabel: { Position: 'Navigation' } })}
                        className={classnames('link', 'menu-create-topic', 'qa-menu-create-topic')}
                        tabIndex={0}
                        role='button'
                      >
                        <span className='icon--pen icon--auto-width' aria-hidden='true' />
                        <span><Phrase p='main.navigation.add_topic' /></span>
                      </a>
                    </li>
                    : null}

                  {!user.id
                    ? <li>
                      {this.props.ssoLoginUrl
                        ? <a href={this.props.ssoLoginUrl} tabIndex={0} role='button' className='link qa-header-login-button'><Phrase m='Common' p='main.navigation.login' /></a>
                        : <a href='#' className={classnames('link', 'js-open-modal', 'qa-header-login-button')} tabIndex={0} role='button' onClick={() => window.app.events.commands.execute('modal:login:show')}><Phrase m='Common' p='main.navigation.login' /></a>}
                    </li>
                    : <Dropdown {...user} size='S' />}
                </ul>
              </section>
            </div>

          </nav>
        )
      }}
      </CurrentUserContext.Consumer>
    )
  }
}

export default NavigationBar
